import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Pagination,
    Button,
    Stack,
    Typography,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { fetchAllProjects, deleteProject, fetchProjectById } from '../controllers/projectsController';
import { resolveProjectsAssetUrl } from '../api/httpClientProjects';
import { resolveGroupsAssetUrl } from '../api/httpClientGroups';
import CreateProjectDialog from './CreateProjectDialog';
import EditProjectDialog from '../components/EditProjectDialog';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const statusLabel = (status) => {
    switch (status) {
        case 0:
            return 'Planlandı';
        case 1:
            return 'Devam Ediyor';
        case 2:
            return 'Tamamlandı';
        case 3:
            return 'Beklemede';
        case 4:
            return 'İptal';
        default:
            return 'Bilinmiyor';
    }
};

const statusColor = (status) => {
    switch (status) {
        case 0:
            return '#0E4D94';
        case 1:
            return '#FF9800';
        case 2:
            return '#17C653';
        case 3:
            return '#FFA500';
        case 4:
            return '#F8285A';
        default:
            return '#666666';
    }
};

const statusBackgroundColor = (status) => {
    switch (status) {
        case 0:
            return '#E7EDF4';
        case 1:
            return '#FFF4E6';
        case 2:
            return '#DFFFEA';
        case 3:
            return '#FFF4E6';
        case 4:
            return '#FFEEF3';
        default:
            return '#f5f5f5';
    }
};

const Projects = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const { data, meta } = await fetchAllProjects({ pageNumber: page, pageSize });
                if (!active) return;
                setItems(Array.isArray(data) ? data : []);
                setTotalPages(meta?.totalPages || 1);
            } catch (e) {
                if (!active) return;
                setError(e?.message || 'Projeler alınamadı');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [page, pageSize]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Projeler</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        color: '#0E4D94',
                        '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                        },
                    }}
                    onClick={() => setCreateOpen(true)}
                >
                    Yeni Proje
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}
                >
                    {loading ? (
                        Array.from({ length: pageSize }).map((_, index) => (
                            <Card key={`skeleton-${index}`} sx={{ width: '100%', position: 'relative', backgroundColor: '#f2f2f2', border: 'none', boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
                                <Skeleton width="100%" height={200} />
                                <CardContent sx={{ p: 2, backgroundColor: '#f2f2f2' }}>
                                    <Stack spacing={1.5}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Skeleton width={80} height={12} />
                                            <Skeleton width={100} height={12} />
                                        </Box>
                                        <Skeleton width="95%" height={20} />
                                        <Skeleton width="100%" height={16} count={3} />
                                        <Skeleton width={100} height={16} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))
                    ) : items.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, gridColumn: '1 / -1' }}>
                            <Typography sx={{ color: '#a3a3a3' }}>Henüz proje eklenmemiş</Typography>
                        </Box>
                    ) : (
                        items.map((p) => {
                            const leader = Array.isArray(p.groupMembers)
                                ? p.groupMembers.find((m) => m.isLeader)
                                : null;
                            return (
                                <Card
                                    key={p.id}
                                    sx={{
                                        width: '100%',
                                        position: 'relative',
                                        backgroundColor: '#f2f2f2',
                                        border: 'none',
                                        boxShadow: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                                        },
                                    }}
                                >
                                    <CardActionArea onClick={() => navigate(`/projects/${p.id}`)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                        {/* Pasif Badge */}
                                        {p.isActive === false && (
                                            <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
                                                <Chip
                                                    label="Pasif"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#FFEEF3',
                                                        color: '#F8285A',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        borderRadius: '12px',
                                                        '& .MuiChip-label': {
                                                            px: 1.5,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        {/* Görsel */}
                                        {p.coverImageUrl && (
                                            <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#ffffff', overflow: 'hidden', position: 'relative' }}>
                                                <Box
                                                    component="img"
                                                    src={resolveProjectsAssetUrl(p.coverImageUrl)}
                                                    alt={p.name}
                                                    sx={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                />
                                            </Box>
                                        )}
                                        {/* İçerik */}
                                        <CardContent sx={{ p: 3, backgroundColor: '#f2f2f2', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 200 }}>
                                            <Stack spacing={1.5} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                {/* Tarih ve Status */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#666666' }}>
                                                        {new Date(p.createdAtUtc).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                    </Typography>
                                                    <Chip
                                                        label={statusLabel(p.status)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: statusBackgroundColor(p.status),
                                                            color: statusColor(p.status),
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            borderRadius: '12px',
                                                            '& .MuiChip-label': {
                                                                px: 1.5,
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                                {/* Başlık */}
                                                <Box sx={{ minHeight: '48px', marginTop: '8px!important', display: 'flex', alignItems: 'flex-start' }}>
                                                    <Typography sx={{ fontWeight: 500, fontSize: 16, color: '#000000' }}>
                                                        {p.name}
                                                    </Typography>
                                                </Box>
                                                {/* Lider Bilgisi */}
                                                {leader && (
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ marginTop: '20px!important' }}>
                                                        <Avatar
                                                            src={resolveGroupsAssetUrl(leader.profilePictureUrl)}
                                                            sx={{ width: 28, height: 28, border: '1px solid #e6e6e6' }}
                                                        >
                                                            {!leader.profilePictureUrl && (
                                                                <Typography sx={{ fontSize: '12px', color: '#0E4D94', fontWeight: 600 }}>
                                                                    {leader.fullName?.charAt(0) || '?'}
                                                                </Typography>
                                                            )}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="caption" sx={{ fontSize: '11px', color: '#a3a3a3', display: 'block' }}>
                                                                Lider
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontSize: 13, color: '#444444', fontWeight: 500 }}>
                                                                {leader.fullName}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                )}
                                                {/* Read Article Link */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginTop: '25px!important' }}>
                                                    <Typography variant="body2" sx={{ fontSize: 13, color: '#0E4D94', fontWeight: 600, borderBottom: '1px solid #0E4D94' }}>
                                                        Projeyi görüntüle
                                                    </Typography>
                                                    <ArrowForwardIcon sx={{ fontSize: 14, color: '#0E4D94' }} />
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </CardActionArea>
                                    <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                                        <Tooltip title="Düzenle">
                                            <IconButton
                                                size="small"
                                                aria-label="Düzenle"
                                                sx={{
                                                    backgroundColor: '#E7EDF4',
                                                    borderRadius: '6px',
                                                    width: 32,
                                                    height: 32,
                                                    '&:hover': {
                                                        backgroundColor: '#CFDBEA',
                                                    },
                                                    '& svg': {
                                                        fontSize: '16px',
                                                        color: '#0E4D94',
                                                    },
                                                }}
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    try {
                                                        const projectData = await fetchProjectById(p.id);
                                                        setSelectedProject(projectData);
                                                        setEditOpen(true);
                                                    } catch (err) {
                                                        alert(err?.message || 'Proje bilgileri alınamadı');
                                                    }
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Sil">
                                            <IconButton
                                                size="small"
                                                aria-label="Sil"
                                                sx={{
                                                    backgroundColor: '#FFEEF3',
                                                    borderRadius: '6px',
                                                    width: 32,
                                                    height: 32,
                                                    '&:hover': {
                                                        backgroundColor: '#FFD6E0',
                                                    },
                                                    '& svg': {
                                                        fontSize: '16px',
                                                        color: '#F8285A',
                                                    },
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelected(p);
                                                    setConfirmOpen(true);
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Card>
                            );
                        })
                    )}
                </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Projeyi Sil
                    <IconButton
                        onClick={() => setConfirmOpen(false)}
                        size="small"
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
                        {selected ? `"${selected.name}" projesini silmek istediğinize emin misiniz?` : 'Seçili projeyi silmek istediğinize emin misiniz?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            color: '#666666',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!selected) return;
                            try {
                                await deleteProject(selected.id);
                                setConfirmOpen(false);
                                setSelected(null);
                                const { data, meta } = await fetchAllProjects({ pageNumber: page, pageSize });
                                setItems(data || []);
                                setTotalPages(meta?.totalPages || 1);
                            } catch (e) { }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            backgroundColor: '#F8285A',
                            borderRadius: '5px',
                            px: 2,
                            '&:hover': {
                                backgroundColor: '#d91e47',
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
            <CreateProjectDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={async () => {
                    setCreateOpen(false);
                    const { data, meta } = await fetchAllProjects({ pageNumber: page, pageSize });
                    setItems(data || []);
                    setTotalPages(meta?.totalPages || 1);
                }}
            />
            <EditProjectDialog
                open={editOpen}
                onClose={() => {
                    setEditOpen(false);
                    setSelectedProject(null);
                }}
                project={selectedProject}
                onSuccess={async () => {
                    setEditOpen(false);
                    setSelectedProject(null);
                    const { data, meta } = await fetchAllProjects({ pageNumber: page, pageSize });
                    setItems(data || []);
                    setTotalPages(meta?.totalPages || 1);
                }}
            />
        </Box>
    );
};

export default Projects;
